import { useCallback } from 'react';

import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { useVideoPlatformCallback } from '../../hooks/useVideoPlatform';
import { AppPlatform } from '../../types';
import { roomAtom } from '../../video-sdk/core/atoms';
import { videoPlatformsMatch } from '../../video-sdk/videoPlatform';
import { waitingVisitorsAtom } from '../state';

export enum VideoPlatformCoercionStrategy {
  /**
   * The provider and visitor are already on matching video platforms, so no coercion is necessary.
   */
  NoOp = 'no-operation',
  /**
   * The provider's system automatically coerces their video platform to match the visitor's video
   * platform. This is only possible if the provider is not already in a room,
   * since joining a room initializes the video SDK and locks in the video
   * platform.
   */
  Auto = 'auto',
  /**
   * Delegating video platform coercion to the visitor's system.
   */
  Delegate = 'delegate',
  /**
   * The provider cannot automatically coerce their video platform to match the
   * visitor's video platform (i.e. in a session), and coercion can't be skipped
   * because the visitor is unable to coerce their video platform to match the
   * provider's (i.e. on Grow iOS app). Additional user action is required.
   */
  Manual = 'manual',
}

/**
 * Identifies a video-platform coercion strategy for the provider when the
 * provider and waiting visitor have different video platforms.
 */
export function useVideoPlatformCoercionStratCallback() {
  const getVideoPlatform = useVideoPlatformCallback();
  const getWaitingVisitorsById = useAtomCallbackMemoized((get) => get(waitingVisitorsAtom), []);
  const getRoom = useAtomCallbackMemoized((get) => get(roomAtom), []);

  return useCallback(
    (visitorId: string) => {
      const isNotInRoom = !getRoom();
      const providerVideoPlatform = getVideoPlatform();
      const { appPlatform: visitorAppPlatform, videoPlatform: visitorVideoPlatform } =
        getWaitingVisitorsById()[visitorId] ?? {};
      const isVisitorUsingIOSApp = visitorAppPlatform === AppPlatform.IOS_APP;
      const isMismatchingVideoPlatforms = !videoPlatformsMatch(visitorVideoPlatform, providerVideoPlatform);

      if (isMismatchingVideoPlatforms) {
        if (isNotInRoom) {
          return VideoPlatformCoercionStrategy.Auto;
        }

        if (isVisitorUsingIOSApp) {
          return VideoPlatformCoercionStrategy.Manual;
        }

        return VideoPlatformCoercionStrategy.Delegate;
      }
      return VideoPlatformCoercionStrategy.NoOp;
    },
    [getRoom, getVideoPlatform, getWaitingVisitorsById],
  );
}
